import * as Sentry from "@sentry/react";
import type { AppDispatch } from "@/app/store";
import { createTabId } from "@/common/utils/helpers";
import { convertDisplayDocumentIdsToUIFormat } from "@/features/patient-view/components/documents/editor/utils";
import {
    updateDocumentInTab,
    resetProgress,
    removeDocumentFromTab,
    removeFromAllDocuments,
} from "./activeDocumentsSlice";

/**
 * Applies a chat_completion state change to the active documents Redux state.
 *
 * Shared by the realtime broadcast handler and the polling fallback so completion
 * is handled identically regardless of transport. Idempotent: applying the same
 * terminal document twice produces no additional visible change.
 */
export function applyDocumentUpdate(
    dispatch: AppDispatch,
    data: { patientRecordId: string; chatCompletion: any },
): void {
    const { patientRecordId, chatCompletion } = data;

    const tabId = createTabId(
        chatCompletion.document_type_id || chatCompletion.document_types?.id,
        chatCompletion.chat_json?.header,
    );

    // is_archived is the server-side "dead" flag. Could come from this client's own
    // optimistic Stop (already removed locally; the dispatches below are idempotent
    // no-ops) OR from a different session (another tab, admin via Spork, mobile). In the
    // second case there was no optimistic remove, so the spinner would stay stuck without
    // this. Either way we must NOT fall through to updateDocumentInTab — a late zombie
    // generation write to an already-archived row would otherwise resurrect it.
    if (chatCompletion?.is_archived) {
        // Log so a non-cancel archive source (retention sweep, patient merge, etc.)
        // silently removing a doc the vet was viewing is at least diagnosable.
        Sentry.addBreadcrumb({
            category: "realtime",
            level: "info",
            message: "Archived chat_completion received — removing from UI",
            data: {
                chatId: chatCompletion.id,
                status: chatCompletion.status,
                patientRecordId,
                tabId: tabId ?? null,
            },
        });
        if (tabId) {
            dispatch(removeDocumentFromTab({ tabId, documentId: chatCompletion.id }));
            dispatch(removeFromAllDocuments({ documentId: chatCompletion.id, patientRecordId }));
            dispatch(resetProgress({ tabId, patientRecordId }));
        }
        return;
    }

    if (tabId) {
        // Realtime payload objects can be frozen; never mutate — shallow-copy before writes.
        let updatedCompletion = chatCompletion;
        if (Array.isArray(chatCompletion?.chat_json?.chat_text)) {
            updatedCompletion = {
                ...chatCompletion,
                chat_json: {
                    ...chatCompletion.chat_json,
                    chat_text: convertDisplayDocumentIdsToUIFormat(chatCompletion.chat_json.chat_text),
                },
            };
        }

        dispatch(updateDocumentInTab({ tabId, document: updatedCompletion, patientRecordId }));
        dispatch(resetProgress({ tabId, patientRecordId }));
    }
}
